import React, { useCallback, useEffect, useMemo, useState } from "react";
import AxiosInstance from "../../AxiosInstance";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { handleAuth } from "../../../components/Login/Auth";
import { getItem } from "../../../utils/storage";
import "./style.css";
import "../CustomerInvoiceJobber.css";
import { Box, Button as MuiButton, Typography, useTheme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  getJobberStatusTone,
  JobberListCard,
  JobberPagination,
  JobberSearch,
  JobberSorting,
  JobberStatusBadge,
  JobberTable,
  JobberTableDocRefTitleCell,
} from "../../../components/MuiTable/index.jsx";
import CustomerNumberTitleStackCell from "../CustomerNumberTitleStackCell";
import CustomerTitleWrapCell from "../CustomerTitleWrapCell";
import { useUserType } from "../../../contexts/UserTypeContext.jsx";

/** Pastel View — mobile collapse Actions row; desktop column hidden via CSS */
const INVOICE_VIEW_BG = "#E0E7FF";
const INVOICE_VIEW_COLOR = "#1E3A8A";

const invoiceMobileActionBtnBase = {
  width: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "8px",
  minHeight: 32,
  px: 0.5,
  py: 0.375,
  lineHeight: 1.15,
  boxShadow: "none",
  whiteSpace: "nowrap",
  justifyContent: "center",
  "& .MuiButton-startIcon": { mr: 0.25, ml: -0.1 },
};

function formatUsd(value) {
  const n = Number(value);
  if (value == null || value === "" || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function invoiceDueNumeric(inv) {
  const raw = inv?.DueAmount ?? inv?.invoiceAccount;
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
}

function invoiceTotalNumeric(inv) {
  const n = Number(inv?.Total);
  return Number.isNaN(n) ? 0 : n;
}

function mergeInvoiceBuckets(inv) {
  // JOB-3177: iterate EVERY bucket on the state object, not just the
  // three legacy buckets. After JOB-3141 the backend returns Draft, Sent,
  // Partial, Past Due, Paid, Canceled, Void — anything we don't iterate
  // here would vanish from the customer portal.
  const map = new Map();
  const add = (row) => {
    const id = row?.InvoiceId;
    if (id == null) return;
    if (!map.has(id)) map.set(id, row);
  };
  Object.values(inv || {}).forEach((bucket) => {
    if (Array.isArray(bucket)) bucket.forEach(add);
  });
  return Array.from(map.values());
}

function invoiceMatchesStatusFilter(row, filterLabel) {
  if (!filterLabel || filterLabel === "All") return true;
  const st = String(row?.Status ?? "").trim();
  if (filterLabel === "Unpaid") return st === "Unpaid" || st === "UnPaid";
  // JOB-3165 + JOB-3178: treat all three variants ("Canceled" / "Cancelled" /
  // "Void") as the same bucket so list + detail views agree.
  if (filterLabel === "Canceled") {
    return st === "Canceled" || st === "Cancelled" || st === "Void";
  }
  return st === filterLabel;
}

// JOB-3177: expand filter dropdown to cover every status the invoice
// pipeline can emit (JOB-3141 introduced Draft / Sent / Partial / Past
// Due / Void alongside the legacy Unpaid / Paid / Canceled).
const CUSTOMER_INVOICE_STATUS_ITEMS = [
  { text: "All" },
  { text: "Draft" },
  { text: "Sent" },
  { text: "Partial" },
  { text: "Past Due" },
  { text: "Unpaid" },
  { text: "Paid" },
  { text: "Canceled" },
  { text: "Void" },
];

function formatInvoiceLocation(loc) {
  if (!loc) return "";
  return [loc?.Address, loc?.City, loc?.State, loc?.Zip].filter(Boolean).join(", ");
}

function jobDescriptionInvoice(inv) {
  const t = inv?.Title || inv?.Subject || inv?.description;
  return (t && String(t).trim()) || "—";
}

/** Same pills as admin invoice list (`JobberStatusBadge` + `getJobberStatusTone`). */
function invoiceStatusBadge(statusRaw) {
  const s = String(statusRaw || "").trim();
  const label =
    s === "UnPaid" ? "Unpaid" : s === "Cancelled" ? "Canceled" : s || "—";
  return (
    <JobberStatusBadge tone={getJobberStatusTone(statusRaw)}>
      {label}
    </JobberStatusBadge>
  );
}

function invoiceStatusRank(status) {
  const s = String(status || "");
  if (s === "Unpaid" || s === "UnPaid") return 1;
  if (s === "Paid") return 2;
  if (s === "Canceled") return 3;
  return 50;
}

function invoicePrimarySortTime(inv) {
  const c = moment(inv?.createdAt);
  if (c.isValid()) return c.valueOf();
  const i = moment(inv?.IssueDate);
  if (i.isValid()) return i.valueOf();
  const u = moment(inv?.UpdatedAt);
  return u.isValid() ? u.valueOf() : 0;
}

function compareInvoices(a, b, field, order) {
  const mul = order === "asc" ? 1 : -1;
  switch (field) {
    case "createdAt": {
      const va = invoicePrimarySortTime(a);
      const vb = invoicePrimarySortTime(b);
      if (va !== vb) return (va - vb) * mul;
      const na = parseInt(a?.InvoiceNumber, 10) || 0;
      const nb = parseInt(b?.InvoiceNumber, 10) || 0;
      return (na - nb) * mul;
    }
    case "InvoiceNumber": {
      const va = parseInt(a?.InvoiceNumber, 10) || 0;
      const vb = parseInt(b?.InvoiceNumber, 10) || 0;
      return (va - vb) * mul;
    }
    case "CompanyName": {
      const va = (a?._companyLabel || "").toLowerCase();
      const vb = (b?._companyLabel || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Property": {
      const va = (formatInvoiceLocation(a?.location) || "").toLowerCase();
      const vb = (formatInvoiceLocation(b?.location) || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Total": {
      const va = invoiceTotalNumeric(a);
      const vb = invoiceTotalNumeric(b);
      if (va !== vb) return (va - vb) * mul;
      return 0;
    }
    case "DueAmount": {
      const va = invoiceDueNumeric(a);
      const vb = invoiceDueNumeric(b);
      if (va !== vb) return (va - vb) * mul;
      return 0;
    }
    case "Title": {
      const va = (jobDescriptionInvoice(a) || "").toLowerCase();
      const vb = (jobDescriptionInvoice(b) || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Status": {
      const ra = invoiceStatusRank(a?.Status);
      const rb = invoiceStatusRank(b?.Status);
      if (ra !== rb) return (ra - rb) * mul;
      return (invoicePrimarySortTime(a) - invoicePrimarySortTime(b)) * mul;
    }
    default:
      return 0;
  }
}

const INVOICE_HEADER_DATA = [
  { label: "Invoice #", field: "InvoiceNumber" },
  { label: "Address", field: "Property", wrap: true },
  { label: "Created On", field: "createdAt" },
  { label: "Total Amount", field: "Total" },
  { label: "Due Amount", field: "DueAmount" },
  { label: "Status", field: "Status" },
  { label: "Actions", field: "" },
];

function CustomerInvoiceList() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  // JOB-3177: buckets are now dynamic — keyed by the invoice's actual
  // `Status`. `mergeInvoiceBuckets` iterates every bucket via
  // Object.values, so new statuses added by the backend are surfaced
  // without a frontend change.
  const [invoices, setInvoices] = useState({});
  const [authPayload, setAuthPayload] = useState({});
  const [loader, setLoader] = useState(true);
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const [listSearch, setListSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  const handleStatusSelect = useCallback((text) => {
    setStatusFilter(text || "All");
  }, []);

  const fetchAuth = useCallback(async () => {
    try {
      const auth = await handleAuth(navigate, location);
      setAuthPayload(auth?.data ?? {});
      setDateDecode(auth?.themes?.Format || "MM/DD/YYYY");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [navigate, location]);

  useEffect(() => {
    fetchAuth();
  }, [fetchAuth]);

  const CustomerId = getItem("CustomerId");
  const companyLabel =
    (authPayload?.CompanyName && String(authPayload.CompanyName).trim()) ||
    (authPayload?.BusinessName && String(authPayload.BusinessName).trim()) ||
    "";

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoader(true);
      try {
        const response = await AxiosInstance.get(`/v1/invoice/invoice/${CustomerId}`);
        if (response?.data?.statusCode === 200) {
          // JOB-3177 / JOB-3189: bucket by the invoice's actual status string
          // so new Phase-5 statuses (Draft/Sent/Partial/Past Due/Void) aren't
          // silently dropped. `mergeInvoiceBuckets` iterates every bucket via
          // Object.values, so any key is surfaced and filter-by-status works.
          const categorizedInvoices = {};
          (response?.data?.data || []).forEach((invoice) => {
            const statusKey = String(invoice?.Status || "Unknown").trim() || "Unknown";
            if (!categorizedInvoices[statusKey]) categorizedInvoices[statusKey] = [];
            categorizedInvoices[statusKey].push(invoice);
          });
          setInvoices(categorizedInvoices);
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setInvoices({});
        }
      } finally {
        setLoader(false);
      }
    };
    if (CustomerId) fetchInvoices();
    else setLoader(false);
  }, [CustomerId]);

  useEffect(() => {
    const backFilter = location?.state?.returnInvoiceStatusFilter;
    if (backFilter != null && String(backFilter).trim() !== "") {
      setStatusFilter(String(backFilter));
    }
  }, [location?.state?.returnInvoiceStatusFilter]);

  useEffect(() => {
    setPage(0);
  }, [listSearch, statusFilter]);

  const merged = useMemo(() => {
    const rows = mergeInvoiceBuckets(invoices);
    return rows.map((inv) => ({
      ...inv,
      _companyLabel: companyLabel || "—",
    }));
  }, [invoices, companyLabel]);

  const statusFiltered = useMemo(
    () => merged.filter((inv) => invoiceMatchesStatusFilter(inv, statusFilter)),
    [merged, statusFilter]
  );

  const invoiceMatchesSearch = useCallback(
    (inv, rawQuery) => {
      const q = (rawQuery || "").trim().toLowerCase();
      if (!q) return true;
      const haystack = [
        String(inv?.InvoiceNumber ?? ""),
        inv?.Title ?? "",
        inv?.Subject ?? "",
        inv?.description ?? "",
        jobDescriptionInvoice(inv),
        companyLabel,
        formatInvoiceLocation(inv?.location),
        inv?.Total != null ? String(inv.Total) : "",
        inv?.DueAmount != null ? String(inv.DueAmount) : "",
        inv?.invoiceAccount != null ? String(inv.invoiceAccount) : "",
        String(inv?.Status ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      return q
        .split(/\s+/)
        .filter(Boolean)
        .every((w) => haystack.includes(w));
    },
    [companyLabel]
  );

  const filtered = useMemo(
    () => statusFiltered.filter((inv) => invoiceMatchesSearch(inv, listSearch)),
    [statusFiltered, listSearch, invoiceMatchesSearch]
  );

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => compareInvoices(a, b, sortField, sortOrder));
    return list;
  }, [filtered, sortField, sortOrder]);

  const countData = sorted.length;

  const pageSlice = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [sorted.length, rowsPerPage, page]);

  const baseUrl = CompanyUrl || "defaultcompany";
  const navigatePath = `/${baseUrl}/customers/invoice-payments`;
  const navigats = [...(location?.state?.navigats || []), "/invoice-payments"];

  const handleViewInvoice = useCallback(
    (invoiceId) => {
      if (invoiceId == null) return;
      const newPath = navigatePath.split("/");
      navigate(navigatePath, {
        state: {
          id: invoiceId,
          navigats: [...navigats, `/${newPath[newPath.length - 1]}`],
          returnInvoiceStatusFilter: statusFilter,
        },
      });
    },
    [navigatePath, navigats, navigate, statusFilter]
  );

  const goToInvoiceDetail = useCallback(
    (e, invoiceId) => {
      if (e) e.stopPropagation();
      handleViewInvoice(invoiceId);
    },
    [handleViewInvoice]
  );

  const cellData = useMemo(
    () =>
      pageSlice.map((inv) => {
        const dateSrc = inv?.createdAt || inv?.IssueDate;
        const createdOnDisplay = formatDateWithUserTimezone(dateSrc, {
          dateFormat: typeof DateDecode === "string" ? DateDecode : "MM/DD/YYYY",
          includeSeconds: false,
        });
        const num = inv?.InvoiceNumber;
        const jobText = jobDescriptionInvoice(inv);
        const addrText = formatInvoiceLocation(inv?.location) || "—";
        const dueVal = inv?.DueAmount ?? inv?.invoiceAccount;
        const iid = inv?.InvoiceId;
        const invoiceLinkColor = isDonationMode
          ? "#29B3E2"
          : isDark
            ? theme.palette.primary.light
            : "#2563EB";
        return {
          key: iid,
          value: [
            <CustomerNumberTitleStackCell
              key={`inv-${iid}`}
              number={num}
              titleText={jobText}
            />,
            <CustomerTitleWrapCell key={`addr-${iid}`} text={addrText} />,
            <Typography
              key={`co-${iid}`}
              sx={{
                whiteSpace: "nowrap",
                fontSize: "0.9375rem",
                color: "text.secondary",
              }}
            >
              {createdOnDisplay || "—"}
            </Typography>,
            <Typography
              key={`tamt-${iid}`}
              sx={{
                fontWeight: 700,
                fontSize: "0.9375rem",
                color: "text.primary",
              }}
            >
              {formatUsd(inv?.Total)}
            </Typography>,
            <Typography
              key={`due-${iid}`}
              sx={{
                fontWeight: 700,
                fontSize: "0.9375rem",
                color: "text.primary",
              }}
            >
              {formatUsd(dueVal)}
            </Typography>,
            invoiceStatusBadge(inv?.Status),
            <Box
              key={`act-${iid}`}
              className="customer-invoice-mobile-actions customer-list-actions-mobile"
              sx={{ display: "grid", width: "100%", maxWidth: "100%" }}
            >
              <MuiButton
                type="button"
                size="small"
                variant="contained"
                disableElevation
                fullWidth
                startIcon={
                  <VisibilityOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: isDonationMode ? "#0f766e" : INVOICE_VIEW_COLOR,
                    }}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewInvoice(iid);
                }}
                sx={{
                  ...invoiceMobileActionBtnBase,
                  ...(isDark && !isDonationMode
                    ? {
                        backgroundColor: "rgba(96, 165, 250, 0.12)",
                        color: theme.palette.primary.light,
                        border: "1px solid rgba(96, 165, 250, 0.35)",
                        "&:hover": {
                          backgroundColor: "rgba(96, 165, 250, 0.2)",
                          borderColor: "rgba(96, 165, 250, 0.5)",
                        },
                      }
                    : {
                        backgroundColor: isDonationMode
                          ? "rgba(104, 178, 203, 0.2)"
                          : INVOICE_VIEW_BG,
                        color: isDonationMode ? "#0f766e" : INVOICE_VIEW_COLOR,
                        border: isDonationMode
                          ? "1px solid rgba(41, 179, 226, 0.3)"
                          : "1px solid rgba(30, 58, 138, 0.2)",
                        "&:hover": {
                          backgroundColor: isDonationMode
                            ? "rgba(104, 178, 203, 0.32)"
                            : "#c7d2fe",
                          borderColor: isDonationMode
                            ? "rgba(41, 179, 226, 0.45)"
                            : "rgba(30, 58, 138, 0.32)",
                        },
                      }),
                }}
              >
                View
              </MuiButton>
            </Box>,
          ],
        };
      }),
    [
      pageSlice,
      DateDecode,
      formatDateWithUserTimezone,
      isDonationMode,
      goToInvoiceDetail,
      isDark,
      theme,
    ]
  );

  const NoSearchResults = () => (
    <Box
      sx={{
        textAlign: "center",
        padding: "48px 20px",
        borderRadius: "16px",
        marginTop: "24px",
        background: isDark ? "rgba(255,255,255,0.04)" : "#f8f9fa",
        border: isDark
          ? "2px dashed rgba(255,255,255,0.12)"
          : "2px dashed #e1e5eb",
      }}
    >
      <SearchIcon
        sx={{
          fontSize: 56,
          color: isDark ? "primary.light" : "#0f2b46",
          marginBottom: 2,
          opacity: 0.55,
        }}
      />
      <Typography
        variant="h6"
        gutterBottom
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        No invoices match your search
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", maxWidth: "400px", margin: "0 auto" }}
      >
        Try a different invoice #, title, address, amount, or status.
      </Typography>
    </Box>
  );

  const NoInvoices = () => (
    <Box
      sx={{
        textAlign: "center",
        padding: "80px 20px",
        borderRadius: "16px",
        marginTop: "24px",
        background: isDark ? "rgba(255,255,255,0.04)" : "#f8f9fa",
        border: isDark
          ? "2px dashed rgba(255,255,255,0.12)"
          : "2px dashed #e1e5eb",
      }}
    >
      <InfoOutlinedIcon
        sx={{ fontSize: 100, color: "var(--amber)", marginBottom: 3, opacity: 0.8 }}
      />
      <Typography
        variant="h5"
        gutterBottom
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        No Invoices Available
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: "text.secondary", maxWidth: "400px", margin: "0 auto" }}
      >
        You haven&apos;t received any invoices yet. Once available, they will show up here.
      </Typography>
    </Box>
  );

  const emptyAll = merged.length === 0;
  const emptyAfterSearch =
    !emptyAll && filtered.length === 0 && (listSearch || "").trim().length > 0;
  const emptyAfterStatusOnly =
    !emptyAll && filtered.length === 0 && !(listSearch || "").trim().length;

  const invoicesToolbar = (
    <Box className="quotesearch gap-2 d-flex customer_searchBar searchBarOfTable">
      <JobberSorting
        triggerVariant="light"
        dropdownItems={CUSTOMER_INVOICE_STATUS_ITEMS}
        placeholder="Select Status"
        value={statusFilter}
        onSelect={handleStatusSelect}
      />
      <JobberSearch search={listSearch} setSearch={setListSearch} />
    </Box>
  );

  const invoicesFooter = (
    <JobberPagination
      totalData={countData}
      currentData={rowsPerPage}
      dataPerPage={rowsPerPage}
      pageItems={[10, 25, 50]}
      page={page}
      setPage={setPage}
      setRowsPerPage={setRowsPerPage}
    />
  );

  const jobberInvoiceMobileProps = {
    showMobileViewDetailsButton: false,
    actionColumnIndex: 6,
    mobileCardPrimaryColumnIndex: 0,
    mobileCardSubtitleColumnIndex: 2,
    mobileCardStatusColumnIndex: 5,
  };

  const invoiceNavigateState = useCallback(
    () => ({
      returnInvoiceStatusFilter: statusFilter,
    }),
    [statusFilter]
  );

  return (
    <Box
      className="customer-invoice-jobber-wrap"
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      {loader ? (
        <JobberListCard
          title="Your Invoices"
          totalCount={0}
          addButton={null}
          toolbar={invoicesToolbar}
          loader={loader}
          footer={invoicesFooter}
        >
          <JobberTable
            headerData={INVOICE_HEADER_DATA}
            cellData={[]}
            {...jobberInvoiceMobileProps}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            sortField={sortField}
            isCollapse={false}
            isNavigate
            navigatePath={navigatePath}
            navigats={navigats}
            getNavigateState={invoiceNavigateState}
          />
        </JobberListCard>
      ) : emptyAll ? (
        <NoInvoices />
      ) : emptyAfterSearch ? (
        <NoSearchResults />
      ) : (
        <JobberListCard
          title="Your Invoices"
          totalCount={countData}
          addButton={null}
          toolbar={invoicesToolbar}
          loader={false}
          footer={invoicesFooter}
        >
          {emptyAfterStatusOnly ? (
            <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, color: "text.primary" }}
              >
                No invoices for the selected status
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                Choose &quot;All&quot; or another status to see more invoices.
              </Typography>
            </Box>
          ) : (
            <JobberTable
              headerData={INVOICE_HEADER_DATA}
              cellData={cellData}
              {...jobberInvoiceMobileProps}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
              sortField={sortField}
              isCollapse={false}
              isNavigate
              navigatePath={navigatePath}
              navigats={navigats}
              getNavigateState={invoiceNavigateState}
            />
          )}
        </JobberListCard>
      )}
    </Box>
  );
}

export default CustomerInvoiceList;
